<!--
 * Path: src/core/components/dialogs/NewProjectDialog.vue
 * Task: Input flow to create new project spaces based on system project schemes, securely styled.
 * Refactored: Activated German input fields and wired them to the payload.
-->
<template>
  <BaseDialog
    :model-value="projectStore.isNewProjectDialogOpen"
    @update:model-value="handleDialogUpdate"
    title="New Project"
    max-width="800"
    @close="handleClose"
  >
    <p class="mb-4 text-medium-emphasis">Create a new project based on a scheme.</p>

    <v-form v-model="newProjectFormValid" ref="newProjectForm">

      <!-- 1. Core Identifiers -->
      <FormSection title="Core Identifiers">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="newProjectData.id"
              label="Project Key (ID)"
              :rules="[
                v => !!v || 'Project Key is required',
                v => /^[A-Z]+$/.test(v) || 'Only uppercase letters are allowed'
              ]"
              @input="newProjectData.id = newProjectData.id.toUpperCase().replace(/[^A-Z]/g, '')"
              variant="outlined"
              density="comfortable"
              required
              hint="Unique identifier (e.g., ERP, CRM). Only uppercase letters."
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="newProjectData.schemeId"
              :items="schemesList"
              label="Select a project scheme"
              :rules="[v => !!v || 'A scheme is required']"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="comfortable"
              required
            >
              <template v-slot:item="{ props: itemProps, item }">
                <v-list-item
                  v-bind="itemProps"
                  :subtitle="undefined"
                >
                  <template v-slot:prepend>
                    <v-icon :color="item.raw.color" class="me-3">{{ item.raw.icon }}</v-icon>
                  </template>
                </v-list-item>
              </template>
            </v-select>
          </v-col>
        </v-row>
      </FormSection>

      <!-- 2. Naming & Summaries -->
      <FormSection title="Naming & Summaries">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="newProjectData.displayName"
              label="Name (EN)"
              :rules="[v => !!v || 'Project name is required']"
              variant="outlined"
              density="comfortable"
              required
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="newProjectData.displayNameGer"
              label="Name (DE)"
              variant="outlined"
              density="comfortable"
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col cols="12" md="6">
            <v-textarea
              v-model="newProjectData.summary"
              label="Summary (EN)"
              rows="2"
              auto-grow
              variant="outlined"
              density="comfortable"
              hide-details
            ></v-textarea>
          </v-col>
          <v-col cols="12" md="6">
            <v-textarea
              v-model="newProjectData.summaryGer"
              label="Summary (DE)"
              rows="2"
              auto-grow
              variant="outlined"
              density="comfortable"
              hide-details
            ></v-textarea>
          </v-col>
        </v-row>
      </FormSection>

      <!-- 3. Visual Styles -->
      <FormSection title="Visuals & UI">
        <IconColorSelector
          v-model:icon="newProjectData.icon"
          v-model:color="newProjectData.color"
          icon-label="Project Icon (Material Design)"
          color-label="Project Color"
          default-icon="mdi-cube"
        />
      </FormSection>

      <!-- 4. Workspace Configuration -->
      <FormSection title="Workspace Configuration">
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="newProjectData.entitySequence"
              label="Entity Sequence"
              type="number"
              variant="outlined"
              density="compact"
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="8" class="d-flex align-center justify-space-around">
            <v-switch v-model="newProjectData.isTemplate" label="Is Template" color="teal" hide-details density="compact"></v-switch>
            <v-switch v-model="newProjectData.isPrivate" label="Is Private" color="warning" hide-details density="compact"></v-switch>
            <v-switch v-model="newProjectData.isSystem" label="Is System" color="primary" hide-details density="compact"></v-switch>
          </v-col>
        </v-row>
      </FormSection>

      <v-alert
        v-if="schemesList.length === 0 && !projectStore.isLoading"
        type="warning"
        variant="tonal"
        density="compact"
        class="mt-4 text-body-2"
      >
        No schemes found or you do not have permission to view them.
      </v-alert>

    </v-form>

    <template #actions>
      <v-btn
        variant="text"
        @click="handleClose"
      >
        Cancel
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        variant="flat"
        @click="handleCreateProject"
        :disabled="!newProjectFormValid"
        :loading="projectStore.isLoading"
      >
        Create
      </v-btn>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useAuthStore } from '@/store/auth';
import { useProjectStore } from '@/store/project';
import BaseDialog from './BaseDialog.vue';
import FormSection from '@/core/components/common/FormSection.vue';
import IconColorSelector from '@/core/components/forms/IconColorSelector.vue';
import { VForm } from 'vuetify/components';

const authStore = useAuthStore();
const projectStore = useProjectStore();

// Strongly typed form ref!
const newProjectForm = ref<InstanceType<typeof VForm> | null>(null);
const newProjectFormValid = ref<boolean>(false);

const newProjectData = ref({
  id: '',
  displayName: '',
  displayNameGer: '',
  summary: '',
  summaryGer: '',
  schemeId: '',
  color: '#424242',
  icon: 'mdi-cube',
  entitySequence: 100,
  isTemplate: false,
  isPrivate: false,
  isSystem: false
});

// Load the schemes directly from the global app configuration
const schemesList = computed(() => {
  const schemes = authStore.appData?.schemes || [];
  return schemes.map((s: any) => ({
    title: s.name_en || s.system_key,
    value: s.system_key,
    color: s.color || 'primary',
    icon: s.icon || 'mdi-dots-grid'
  }));
});

const resetForm = () => {
  newProjectData.value = {
    id: '',
    displayName: '',
    displayNameGer: '',
    summary: '',
    summaryGer: '',
    schemeId: '',
    color: '#424242',
    icon: 'mdi-cube',
    entitySequence: 100,
    isTemplate: false,
    isPrivate: false,
    isSystem: false
  };
  if (newProjectForm.value) {
    newProjectForm.value.resetValidation();
  }
};

const handleDialogUpdate = (isOpen: boolean) => {
  projectStore.isNewProjectDialogOpen = isOpen;
  if (isOpen) {
    resetForm();
  }
};

const handleClose = () => {
  projectStore.isNewProjectDialogOpen = false;
  resetForm();
};

const handleCreateProject = async (): Promise<void> => {
  if (!newProjectForm.value) return;

  const { valid } = await newProjectForm.value.validate();
  if (valid) {
    try {
      // Resolve the visibility string based on the flags
      let visibility = 'public';
      if (newProjectData.value.isTemplate) visibility = 'template';
      else if (newProjectData.value.isPrivate) visibility = 'restricted';

      await projectStore.createProject({
        id: newProjectData.value.id,
        displayName: newProjectData.value.displayName,
        displayNameGer: newProjectData.value.displayNameGer,
        summary: newProjectData.value.summary,
        summaryGer: newProjectData.value.summaryGer,
        visibility: visibility,
        schemeId: newProjectData.value.schemeId,
        color: newProjectData.value.color,
        icon: newProjectData.value.icon,
        isSystem: newProjectData.value.isSystem,
        entitySequence: newProjectData.value.entitySequence
      });
      handleClose();
    } catch (error: any) {
      authStore.showNotification(`Error creating project: ${error.message}`, 'error');
    }
  } else {
    authStore.showNotification('Please fill in all required fields and select a scheme.', 'warning');
  }
};
</script>
